import React, {useImperativeHandle} from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import {Constraint} from '@aperza/validator';
import {useFieldMap, Message} from '@aperza/ui';
import Container from "react-bootstrap/Container";
import FormRow from "../form/FormRow";
import FormField from "../form/FormField";
import UserBusinessTypeField from './UserBusinessTypeField';
import UserJobCategoryTypeField from './UserJobCategoryTypeField';
import UserSubdivisionField from './UserSubdivisionField';

const UserInfoForm = React.forwardRef((props, ref) => {

  let {
    viewer = {},
    readOnly,
    onUpdateFieldMap,
  } = props;

  let {
    user = {},
  } = viewer;

  useImperativeHandle(ref, () => ({
    validate: () => {
      return validateFieldMap();
    }
  }));

  let config = {
    firstName: {
      fieldName: 'firstName',
      constraint: Constraint.firstName,
      value: user.firstName ? user.firstName : '',
    },
    lastName: {
      fieldName: 'lastName',
      constraint: Constraint.lastName,
      value: user.lastName ? user.lastName : '',
    },
    phoneNumber: {
      fieldName: 'phoneNumber',
      constraint: Constraint.phoneNumber,
      value: user.phoneNumber ? user.phoneNumber : '',
    },
    companyName: {
      fieldName: 'companyName',
      constraint: Constraint.companyName,
      value: user.companyName ? user.companyName : '',
    },
    officeName: {
      fieldName: 'officeName',
      constraint: Constraint.officeName,
      value: user.officeName ? user.officeName : '',
    },
    departmentName: {
      fieldName: 'departmentName',
      constraint: Constraint.departmentName,
      value: user.departmentName ? user.departmentName : '',
    },
    businessType: {
      fieldName: 'businessType',
      constraint: Constraint.businessType,
      value: user.businessType ? user.businessType : '',
    },
    jobCategoryType: {
      fieldName: 'jobCategoryType',
      constraint: Constraint.jobCategoryType,
      value: user.jobCategoryType?user.jobCategoryType:'',
    },
    subdivisionCode: {
      fieldName: 'subdivisionCode',
      constraint: Constraint.subdivisionCode,
      value: user.subdivisionCode?user.subdivisionCode:'',
    },

  };

  let {fieldMap, updateFieldValue, validateFieldMap, validateField} = useFieldMap(
    config,
    {}
  );

  const onBlur = (fieldName, value) => {
    validateField(fieldName);
  };

  const onChange = (fieldName, value) => {
    updateFieldValue(fieldName, value);
    if (fieldName === 'business_types') {
      updateFieldValue('jobCategoryType', undefined);
    }
    validateField(fieldName);
  };

  return (
    <Container fluid="true" className="aw-form mb-5" id="customerInfoForm">
      <h4 className="font-weight-bold text-primary"><Message messageId="customerInfo"/></h4>
      <FormRow rowNameId={'last_name'} required={true}>
        <FormField
          readOnly={readOnly}
          fieldName={'lastName'}
          placeholderId={'ex_last_name'}
          {...fieldMap['lastName']}
          onBlur={onBlur}
          onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'first_name'} required={true}>
        <FormField
          readOnly={readOnly}
          fieldName={'firstName'}
          placeholderId={'ex_first_name'}
          {...fieldMap['firstName']}
          onBlur={onBlur}
          onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'phone_number'} required={true}>
        <FormField
          readOnly={readOnly}
          remarkId={'phone_number_valid'}
          fieldName={'phoneNumber'}
          placeholderId={'ex_phone_number'}
          {...fieldMap['phoneNumber']}
          onBlur={onBlur}
          onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'companyName'} required={true}>
        <FormField
          readOnly={readOnly}
          fieldName={'companyName'}
          placeholderId={'ex_company_name'}
          {...fieldMap['companyName']}
          onBlur={onBlur}
          onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'officeName'}>
        <FormField
          readOnly={readOnly}
          fieldName={'officeName'}
          placeholderId={'ex_office_name'}
          {...fieldMap['officeName']}
          onBlur={onBlur}
          onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'department'} required={true}>
        <FormField
          readOnly={readOnly}
          fieldName={'departmentName'}
          // remarkId={'department_valid'}
          placeholderId={'ex_department'}
          {...fieldMap['departmentName']}
          onBlur={onBlur}
          onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'businessTypes'} required={true}>
        <UserBusinessTypeField businessTypes={viewer.businessTypes}
                               readOnly={readOnly}
                               field={fieldMap['businessType']}
                               onBlur={onBlur}
                               onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'jobCategoryTypes'} required={true}>
        <UserJobCategoryTypeField businessType={fieldMap['businessType'].value}
                              readOnly={readOnly}
                              field={fieldMap['jobCategoryType']}
                              onBlur={onBlur}
                              onChange={onChange}/>
      </FormRow>
      <FormRow rowNameId={'subdivisionCode'} required={true}>
        <UserSubdivisionField subdivisionCodes={viewer.subdivisionCodes}
                              readOnly={readOnly}
                              field={fieldMap['subdivisionCode']}
                              onBlur={onBlur}
                              onChange={onChange}/>
      </FormRow>
    </Container>

  )

});

UserInfoForm.fragments = {
  viewer: gql`
      fragment UserInfoForm_viewer on Viewer {
          id
          user {
              id
              aperzaId
              firstName
              lastName
              phoneNumber
              companyName
              officeName
              departmentName
              businessType
              jobCategoryType
              subdivisionCode
          }
          businessTypes {
              ...UserBusinessTypeField_businessTypes
          }
          subdivisionCodes {
              ...UserSubdivisionField_subdivisionCodes
          }
      }
      ${UserBusinessTypeField.fragments.businessTypes}
      ${UserSubdivisionField.fragments.subdivisionCodes}
  `
};

export default UserInfoForm;
